// Zayna Home — predictive search + full-page search pagination.
(() => {
  'use strict';

  if (window.__zhSearchInit) return;
  window.__zhSearchInit = true;

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const initializedForms = new WeakSet();
  const initializedPages = new WeakSet();


  const searchTypoCorrections = new Map([
    ['pit', 'pot'], ['ppt', 'pot'], ['ppot', 'pot'], ['pott', 'pot'], ['pto', 'pot'], ['oot', 'pot'], ['poy', 'pot'],
    ['potn', 'potten'], ['pottenn', 'potten'], ['kruidepot', 'kruidenpot'], ['kruidenpt', 'kruidenpot'], ['voorraadpt', 'voorraadpot'], ['vooraadpot', 'voorraadpot'], ['bewaarpt', 'bewaarpot'],
    ['doe', 'doek'], ['doke', 'doek'], ['doej', 'doek'], ['deok', 'doek'], ['doekn', 'doeken'], ['doekenn', 'doeken'], ['keukendoekn', 'keukendoeken'], ['keuekendoek', 'keukendoek'], ['theedoekn', 'theedoeken'],
    ['gls', 'glas'], ['gals', 'glas'], ['glaz', 'glas'], ['glasz', 'glas'], ['gkas', 'glas'], ['drinkgls', 'drinkglas'],
    ['bkr', 'beker'], ['bekr', 'beker'], ['beeker', 'beker'], ['bekker', 'beker'], ['bekerss', 'bekers'],
    ['olue', 'olie'], ['olir', 'olie'], ['oliee', 'olie'], ['olii', 'olie'], ['olieflesse', 'olieflessen'],
    ['fls', 'fles'], ['flesz', 'fles'], ['flese', 'fles'], ['fless', 'fles'], ['flessenn', 'flessen'],
    ['mko', 'mok'], ['mokj', 'mok'], ['mokk', 'mok'], ['moken', 'mokken'],
    ['kpje', 'kopje'], ['kopj', 'kopje'], ['kopjeh', 'kopje'], ['kopjesz', 'kopjes'], ['koffiekpj', 'koffiekopje'],
    ['the', 'thee'], ['theee', 'thee'], ['theeë', 'thee'], ['thepot', 'theepot'], ['theepott', 'theepot'],
    ['karf', 'karaf'], ['kraaf', 'karaf'], ['karaff', 'karaf'], ['karafg', 'karaf'],
    ['hak', 'haak'], ['haakj', 'haak'], ['haakn', 'haken'], ['wandhak', 'wandhaak'],
    ['bestk', 'bestek'], ['bstek', 'bestek'], ['bestekj', 'bestek'], ['bstiek', 'bestek'],
    ['rietj', 'rietje'], ['rietjeh', 'rietje'], ['rietjesz', 'rietjes'], ['rietjs', 'rietjes'],
    ['onderzeter', 'onderzetter'], ['onderzettr', 'onderzetter'], ['pannenonderzeter', 'pannenonderzetter'],
    ['maatlepls', 'maatlepels'], ['maatlepelz', 'maatlepels'], ['weeglepl', 'weeglepel'],
    ['ovenwamt', 'ovenwant'], ['ovenwanr', 'ovenwant'], ['ovenhandschoe', 'ovenhandschoen'],
    ['honig', 'honing'], ['honning', 'honing'], ['honingpt', 'honingpot'],
    ['zeepomp', 'zeeppomp'], ['zeeppom', 'zeeppomp'], ['zeepdispenserr', 'zeepdispenser'],
    ['barbeque', 'barbecue'], ['bbqspies', 'barbecuespies'], ['barbecuespiez', 'barbecuespies'],
    ['avacodo', 'avocado'], ['avokado', 'avocado'], ['avocadosnijdr', 'avocadosnijder'],
    ['miniprint', 'miniprinter'], ['labelprint', 'labelprinter'], ['labelprinterr', 'labelprinter'],
  ]);

  function normalizeSearchKey(value) {
    return String(value || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLocaleLowerCase('nl-NL')
      .trim()
      .replace(/[“”"'`]+/g, '')
      .replace(/[.,;:!?]+$/g, '')
      .replace(/\s+/g, ' ');
  }

  function normalizeSearchTerm(value) {
    const raw = String(value || '').trim().replace(/\s+/g, ' ');
    const key = normalizeSearchKey(raw);
    const corrected = searchTypoCorrections.get(key) || raw;

    return {
      raw,
      query: corrected,
      corrected: Boolean(raw && corrected !== raw),
    };
  }

  function loadingMarkup() {
    let rows = '';

    for (let index = 0; index < 4; index += 1) {
      rows += `
        <li class="zh-search-skeleton__row">
          <span class="zh-search-skeleton__image"></span>
          <span class="zh-search-skeleton__copy">
            <span class="zh-search-skeleton__line zh-search-skeleton__line--wide"></span>
            <span class="zh-search-skeleton__line zh-search-skeleton__line--short"></span>
          </span>
        </li>
      `;
    }

    return `
      <div class="zh-search-skeleton${reduceMotion ? '' : ' is-animated'}" aria-hidden="true">
        <span class="zh-search-skeleton__label"></span>
        <ul>${rows}</ul>
      </div>
      <p class="sr-only">Zoekresultaten worden geladen.</p>
    `;
  }

  function errorMarkup() {
    return `
      <div class="zh-search-message" role="status">
        <p class="zh-search-message__title">Zoeken lukt op dit moment niet.</p>
        <p class="zh-search-message__text">Controleer je verbinding en probeer het opnieuw.</p>
      </div>
    `;
  }

  function minimumMarkup(minimum) {
    return `
      <p class="zh-search-status">
        Typ minimaal ${minimum} tekens om te zoeken.
      </p>
    `;
  }

  function initPredictiveForm(form) {
    if (!(form instanceof HTMLFormElement) || initializedForms.has(form)) return;

    const input = form.querySelector('[data-predictive-input]');
    const searchAside = form.closest('[data-aside="search"]');
    const region = searchAside?.querySelector('[data-predictive-results]') ||
      document.querySelector('[data-predictive-results]');

    if (!(input instanceof HTMLInputElement) || !(region instanceof HTMLElement)) return;

    initializedForms.add(form);

    const startMarkup = region.innerHTML;
    const suggestUrl = form.dataset.predictiveUrl || '/search/suggest';
    const minimum = Math.max(1, Number.parseInt(form.dataset.minChars || '2', 10) || 2);
    const sectionId = 'predictive-search';
    const limit = 8;
    const cache = new Map();
    const cacheLimit = 18;

    let timer = null;
    let lastTerm = '';
    let controller = null;

    const cacheKey = (term) => term.toLocaleLowerCase('nl-NL').trim();

    const resultLinks = () => Array.from(
      region.querySelectorAll('[data-predictive-link], a[href]')
    ).filter((link) => link instanceof HTMLAnchorElement && !link.closest('[hidden]'));

    function setExpanded(expanded) {
      input.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    }

    function setRegionHTML(markup) {
      region.classList.remove('is-refreshing');
      region.removeAttribute('aria-busy');
      region.innerHTML = markup;
    }

    function remember(term, markup) {
      const key = cacheKey(term);
      if (!key || !markup) return;

      cache.set(key, markup);

      if (cache.size > cacheLimit) {
        cache.delete(cache.keys().next().value);
      }
    }

    function reset() {
      if (timer) window.clearTimeout(timer);
      timer = null;

      if (controller) controller.abort();
      controller = null;
      lastTerm = '';
      setRegionHTML(startMarkup);
      setExpanded(false);
    }

    function renderResponse(html, term) {
      const documentFragment = new DOMParser().parseFromString(html, 'text/html');
      const fresh = documentFragment.querySelector('[data-predictive-results-list]');

      if (!fresh) {
        setRegionHTML(errorMarkup());
        setExpanded(true);
        return;
      }

      const markup = fresh.outerHTML;
      remember(term, markup);
      setRegionHTML(markup);
      setExpanded(true);
    }

    function showPendingState() {
      const hasCurrentResults = Boolean(
        region.querySelector('[data-predictive-results-list]')
      );

      if (hasCurrentResults) {
        region.classList.add('is-refreshing');
        region.setAttribute('aria-busy', 'true');
        return;
      }

      setRegionHTML(loadingMarkup());
      setExpanded(true);
    }

    async function run(term) {
      if (controller) controller.abort();
      controller = new AbortController();

      const params = new URLSearchParams();
      params.set('q', term);
      params.set('section_id', sectionId);
      params.set('resources[type]', 'product');
      params.set('resources[limit]', String(limit));
      params.set('resources[limit_scope]', 'each');
      params.set('resources[options][fields]', 'title,product_type,variants.title,tag');
      params.set('resources[options][unavailable_products]', 'last');

      try {
        const response = await fetch(`${suggestUrl}?${params.toString()}`, {
          headers: { 'X-Requested-With': 'fetch' },
          signal: controller.signal,
        });

        if (!response.ok) throw new Error(`Predictive search failed: ${response.status}`);
        renderResponse(await response.text(), term);
      } catch (error) {
        if (error.name === 'AbortError') return;
        console.error(error);
        setRegionHTML(errorMarkup());
        setExpanded(true);
      } finally {
        controller = null;
      }
    }

    input.addEventListener('input', () => {
      const normalized = normalizeSearchTerm(input.value);
      const term = normalized.query.trim();
      const key = cacheKey(term);

      if (timer) window.clearTimeout(timer);
      if (controller) controller.abort();

      if (!normalized.raw) {
        reset();
        return;
      }

      if (term.length < minimum) {
        lastTerm = '';
        setRegionHTML(minimumMarkup(minimum));
        setExpanded(false);
        return;
      }

      if (key && cache.has(key)) {
        lastTerm = term;
        setRegionHTML(cache.get(key));
        setExpanded(true);
        return;
      }

      if (term === lastTerm && region.classList.contains('is-refreshing')) return;

      lastTerm = term;
      showPendingState();
      timer = window.setTimeout(() => run(term), 150);
    });

    input.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && input.value) {
        event.stopPropagation();
        input.value = '';
        reset();
        return;
      }

      if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;

      const links = resultLinks();
      if (!links.length) return;

      event.preventDefault();
      const target = event.key === 'ArrowDown' ? links[0] : links[links.length - 1];
      target.focus({ preventScroll: true });
    });

    region.addEventListener('click', (event) => {
      if (event.target.closest('[data-cart-add-form], [data-cart-add-button], button')) {
        return;
      }

      const link = event.target.closest('[data-predictive-link], a[href]');
      if (!(link instanceof HTMLAnchorElement)) return;

      /* Preserve native new-tab and modified-click behaviour. */
      if (
        event.defaultPrevented ||
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      ) {
        return;
      }

      event.preventDefault();
      window.location.assign(link.href);
    });

    region.addEventListener('keydown', (event) => {
      const current = event.target.closest('[data-predictive-link], a[href]');
      if (!(current instanceof HTMLAnchorElement)) return;

      const links = resultLinks();
      const currentIndex = links.indexOf(current);
      if (currentIndex < 0) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        input.focus({ preventScroll: true });
        return;
      }

      let nextIndex = null;

      if (event.key === 'ArrowDown') nextIndex = Math.min(currentIndex + 1, links.length - 1);
      else if (event.key === 'ArrowUp') nextIndex = Math.max(currentIndex - 1, 0);
      else if (event.key === 'Home') nextIndex = 0;
      else if (event.key === 'End') nextIndex = links.length - 1;

      if (nextIndex === null) return;

      event.preventDefault();
      links[nextIndex].focus({ preventScroll: true });
    });

    form.addEventListener('submit', (event) => {
      const normalized = normalizeSearchTerm(input.value);

      if (!normalized.raw) {
        event.preventDefault();
        input.focus();
        return;
      }

      if (normalized.corrected) {
        input.value = normalized.query;
      }
    });

    document.addEventListener('aside:close', (event) => {
      if (event.detail?.type === 'search') reset();
    });
  }

  function initPredictiveSearch(scope = document) {
    scope.querySelectorAll('[data-predictive-form]').forEach(initPredictiveForm);
  }


  function normalizeSearchPageUrl() {
    const url = new URL(window.location.href);
    const pageValue = Number.parseInt(url.searchParams.get('page') || '1', 10);
    let shouldReplace = false;

    if (!url.pathname.includes('/search')) {
      return false;
    }

    if (Number.isFinite(pageValue) && pageValue > 1) {
      url.searchParams.delete('page');
      shouldReplace = true;
    }

    const currentTerm = url.searchParams.get('q') || '';
    const normalized = normalizeSearchTerm(currentTerm);

    if (normalized.corrected) {
      url.searchParams.set('q', normalized.query);
      url.searchParams.set('type', 'product');
      url.searchParams.delete('page');
      shouldReplace = true;
    }

    if (!shouldReplace) {
      return false;
    }

    window.location.replace(url.toString());
    return true;
  }

  // Predictive result links remain native links. The previous implementation
  // closed and inerted the search drawer before the browser completed the
  // navigation, which made product results appear unclickable.

  document.addEventListener('keydown', (event) => {
    if (event.key.toLowerCase() !== 'k' || (!event.metaKey && !event.ctrlKey)) return;

    event.preventDefault();
    const trigger = document.querySelector('[data-aside-open="search"]');
    if (trigger instanceof HTMLElement) trigger.click();
  });

  function initSearchPage(root) {
    if (!(root instanceof HTMLElement) || initializedPages.has(root)) return;

    if (normalizeSearchPageUrl()) return;

    const sectionId = root.getAttribute('data-section-id');
    const baseUrl = root.getAttribute('data-search-url');
    if (!sectionId || !baseUrl) return;

    initializedPages.add(root);

    let busy = false;
    let observer = null;

    function bindInfinite() {
      if (observer) {
        observer.disconnect();
        observer = null;
      }

      const sentinel = root.querySelector('[data-load-more]');
      if (!(sentinel instanceof HTMLElement)) return;

      const link = sentinel.querySelector('[data-load-more-link]');
      if (!(link instanceof HTMLAnchorElement)) return;

      async function loadNext() {
        if (busy) return;

        busy = true;
        sentinel.setAttribute('aria-busy', 'true');

        const textElement = link.querySelector('[data-load-more-text]');
        const loadingLabel = link.getAttribute('data-loading-label');
        const originalLabel = textElement?.textContent || '';

        if (textElement && loadingLabel) textElement.textContent = loadingLabel;

        try {
          const href = link.getAttribute('href') || '';
          const query = href.split('?')[1] || '';
          const params = new URLSearchParams(query);
          params.set('section_id', sectionId);

          const response = await fetch(`${baseUrl}?${params.toString()}`, {
            headers: { 'X-Requested-With': 'fetch' },
          });

          if (!response.ok) throw new Error(`Load more failed: ${response.status}`);

          const freshDocument = new DOMParser().parseFromString(
            await response.text(),
            'text/html'
          );
          const freshGrid = freshDocument.querySelector('[data-product-grid]');
          const currentGrid = root.querySelector('[data-product-grid]');

          if (freshGrid && currentGrid) {
            while (freshGrid.firstElementChild) {
              currentGrid.appendChild(freshGrid.firstElementChild);
            }
          }

          const freshSentinel = freshDocument.querySelector('[data-load-more]');
          if (freshSentinel) sentinel.replaceWith(freshSentinel);
          else sentinel.remove();

        } catch (error) {
          console.error(error);
          if (textElement) {
            textElement.textContent = link.getAttribute('data-retry-label') || 'Opnieuw proberen';
          }
          sentinel.removeAttribute('aria-busy');
          busy = false;
          return;
        }

        busy = false;
        if (textElement && originalLabel) textElement.textContent = originalLabel;
        bindInfinite();
      }

      link.addEventListener('click', (event) => {
        event.preventDefault();
        loadNext();
      });

      if ('IntersectionObserver' in window && !reduceMotion) {
        observer = new IntersectionObserver(
          (entries) => {
            if (entries[0]?.isIntersecting) loadNext();
          },
          { rootMargin: '600px 0px' }
        );
        observer.observe(sentinel);
      }
    }

    bindInfinite();
  }

  function initSearchPages(scope = document) {
    scope.querySelectorAll('[data-search]').forEach(initSearchPage);
  }

  initPredictiveSearch();
  initSearchPages();

  document.addEventListener('shopify:section:load', (event) => {
    initPredictiveSearch(event.target);
    initSearchPages(event.target);
  });
})();
